/**
 * INNFLOW DESIGN SYSTEM V2.0 - Avatar Component
 * 
 * Componente versátil para exibição de usuários, agentes e bots
 * Conforme especificação INNFLOW System Architecture
 * 
 * @version 2.0.0
 * @author INNFLOW Design Team
 */

import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { StatusDot, StatusDotVariant } from './StatusDot';
import './Avatar.css';

export type AvatarColor = 'green' | 'blue' | 'violet' | 'red' | 'yellow' | 'cyan' | 'neutral';
export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export interface AvatarProps {
  size?: AvatarSize;
  color?: AvatarColor;
  src?: string;
  alt?: string;
  initials?: string;
  icon?: React.ReactNode;
  bordered?: boolean;
  status?: StatusDotVariant;
  className?: string;
  'aria-label'?: string;
}

/**
 * Avatar - Componente para exibição de avatares
 * 
 * Suporta:
 * - Imagem (src)
 * - Iniciais (initials)
 * - Ícone customizado (icon)
 * - StatusDot integrado
 * - 7 cores de fundo
 * - 6 tamanhos
 */
export const Avatar: React.FC<AvatarProps> = ({
  size = 'md',
  color = 'green',
  src,
  alt,
  initials,
  icon,
  bordered = false,
  status,
  className,
  'aria-label': ariaLabel,
}) => {
  const [imageError, setImageError] = useState(false);
  const showImage = src && !imageError;

  const getInitials = () => {
    if (initials) return initials.toUpperCase().slice(0, 2);
    if (alt) {
      const parts = alt.split(' ');
      return parts.slice(0, 2).map(p => p[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U';
  };

  return (
    <div
      className={cn(
        'innflow-avatar-wrapper',
        className
      )}
    >
      <div
        className={cn(
          'innflow-avatar',
          `innflow-avatar--${size}`,
          `innflow-avatar--${color}`,
          {
            'innflow-avatar--bordered': bordered,
          }
        )}
        role="img"
        aria-label={ariaLabel || alt || 'Avatar'}
      >
        {showImage ? (
          <img
            src={src}
            alt={alt || 'Avatar'}
            className="innflow-avatar__image"
            onError={() => setImageError(true)}
            loading="lazy"
          />
        ) : icon ? (
          <span className="innflow-avatar__icon" aria-hidden="true">
            {icon}
          </span>
        ) : (
          <span className="innflow-avatar__initials" aria-hidden="true">
            {getInitials()}
          </span>
        )}
      </div>
      {status && (
        <StatusDot
          variant={status}
          size={size === 'xs' ? 'xs' : size === 'sm' ? 'sm' : 'md'}
          className="innflow-avatar__status"
        />
      )}
    </div>
  );
};

export default Avatar;
